import React, { useRef, useState } from "react";
import { Upload, Trash2, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { thumbUrl } from "@/api";
import { PALETTE } from "@/lib/tint";

export const COLOR_PRESETS = PALETTE.map(({ label, hex }) => ({ label, hex }));

export function DropZone({ onFiles, multiple = true, label, testid, disabled }) {
  const ref = useRef();
  const [over, setOver] = useState(false);
  const pick = (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith("image/") || /\.(png|jpe?g|webp|heic)$/i.test(f.name));
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <>
      <div
        onClick={() => !disabled && ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) pick(e.dataTransfer.files); }}
        data-testid={testid}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 flex flex-col items-center justify-center text-center gap-2 transition-colors ${
          over ? "border-[#C58B90] bg-[#F7EBEC]" : "border-[#E8CFD1] bg-[#FCF6F6]"
        }`}
      >
        <Upload size={26} className="text-[#C58B90]" />
        <p className="text-xs text-[#7A7571]">{label}</p>
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/*"
        multiple={multiple}
        data-testid={`${testid}-input`}
        className="hidden"
        onChange={(e) => { pick(e.target.files); e.target.value = ""; }}
      />
    </>
  );
}

const newKey = () => Math.random().toString(36).slice(2, 8);

const toRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

// Average color of opaque pixels -> nearest preset
const guessPreset = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = c.height = 48;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, 48, 48);
      const d = ctx.getImageData(0, 0, 48, 48).data;
      let r = 0, g = 0, b = 0, n = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] > 128) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
      }
      if (!n) return resolve(null);
      const avg = [r / n, g / n, b / n];
      let best = null, bestD = Infinity;
      COLOR_PRESETS.forEach((p) => {
        const dist = toRgb(p.hex).reduce((s, v, k) => s + (v - avg[k]) ** 2, 0);
        if (dist < bestD) { bestD = dist; best = p; }
      });
      resolve(best);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

export const variationsFromSticker = (s) => {
  if (Array.isArray(s.variations) && s.variations.length) return s.variations.map((v) => ({ ...v }));
  return [{ key: newKey(), label: "Original", hex: "#FFFFFF", storage_path: s.storage_path }];
};

export function VariationsEditor({ value, onChange }) {
  const addFiles = async (files) => {
    const added = await Promise.all(files.map(async (file) => {
      const preview = URL.createObjectURL(file);
      const p = (await guessPreset(preview)) || COLOR_PRESETS[0];
      return { key: newKey(), label: p.label, hex: p.hex, file, preview };
    }));
    onChange([...value, ...added]);
  };
  const update = (key, patch) => onChange(value.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  const remove = (key) => onChange(value.filter((v) => v.key !== key));
  const move = (from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(next);
  };
  const [dragIdx, setDragIdx] = useState(null);
  const dropOn = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...value];
    const [item] = next.splice(dragIdx, 1);
    next.splice(idx, 0, item);
    setDragIdx(null);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="variations-editor">
      <p className="text-sm font-medium">Cores da figurinha <span className="text-[#7A7571] font-normal">({value.length})</span></p>
      <p className="text-xs text-[#7A7571] -mt-2">Todas as figurinhas já ganham automaticamente a paleta completa de cores na vitrine. Envie PNGs aqui só se quiser usar a sua própria versão de uma cor (ela substitui a automática). Arraste ou use as setas para ordenar — a primeira é a principal.</p>
      {value.map((v, idx) => (
        <div
          key={v.key}
          draggable
          onDragStart={() => setDragIdx(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropOn(idx); }}
          onDragEnd={() => setDragIdx(null)}
          className={`relative flex items-center gap-3 p-2.5 rounded-2xl border bg-white transition-colors ${dragIdx === idx ? "border-[#C58B90] opacity-60" : "border-[#F2E6E7]"}`}
          data-testid={`variation-row-${idx}`}
        >
          <GripVertical size={16} className="text-[#D8BFC1] shrink-0 cursor-grab hidden sm:block" />
          {idx === 0 && (
            <span className="absolute -top-2 left-3 bg-[#C58B90] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid="variation-main-badge">Principal</span>
          )}
          <div className="sticker-canvas h-16 w-16 shrink-0 rounded-xl flex items-center justify-center overflow-hidden">
            <img src={v.preview || thumbUrl(v.storage_path)} alt={v.label} className="h-[85%] w-[85%] object-contain" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={v.hex}
                onChange={(e) => update(v.key, { hex: e.target.value })}
                data-testid={`variation-hex-${idx}`}
                className="h-8 w-8 rounded-full border border-[#E8CFD1] cursor-pointer bg-white shrink-0"
              />
              <input
                value={v.label}
                onChange={(e) => update(v.key, { label: e.target.value })}
                placeholder="Nome da cor"
                data-testid={`variation-label-${idx}`}
                className="flex-1 min-w-0 h-9 px-3 rounded-xl bg-[#FCFBF9] border border-[#E8CFD1] outline-none focus:border-[#C58B90] text-sm"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {COLOR_PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.hex}
                  title={p.label}
                  onClick={() => update(v.key, { hex: p.hex, label: p.label })}
                  data-testid={`variation-preset-${idx}-${p.label}`}
                  className={`h-5 w-5 rounded-full border ${v.hex.toLowerCase() === p.hex.toLowerCase() ? "ring-2 ring-offset-1 ring-[#C58B90]" : "border-black/15"}`}
                  style={{ backgroundColor: p.hex }}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center shrink-0">
            <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`variation-up-${idx}`} className="p-1.5 text-[#C58B90] disabled:opacity-25" aria-label="Subir cor">
              <ChevronUp size={16} />
            </button>
            <button type="button" onClick={() => remove(v.key)} data-testid={`variation-remove-${idx}`} className="p-1.5 text-[#C0555A]" aria-label="Remover cor">
              <Trash2 size={15} />
            </button>
            <button type="button" onClick={() => move(idx, 1)} disabled={idx === value.length - 1} data-testid={`variation-down-${idx}`} className="p-1.5 text-[#C58B90] disabled:opacity-25" aria-label="Descer cor">
              <ChevronDown size={16} />
            </button>
          </div>
        </div>
      ))}
      <DropZone onFiles={addFiles} testid="variation-drop" label="Arraste ou toque para adicionar PNG(s) de cor" />
    </div>
  );
}
